import React, { useState, useEffect } from 'react';
import Header from '../../components/Header';
import { useAuthStore } from '../../features/authentication/stores/authStore';
import { ApprovalStatus, Preset } from '../main/components/Preset/models';
import { Role } from '../../features/authentication/types/auth.types';
import StatusFilter, { PresetFilter } from './components/StatusFilter';
import UserFilter, { USER_FILTER_ALL } from './components/UserFilter';
import SortDropdown, { SortOption } from './components/SortDropdown';
import myGif from '../../assets/throbber.gif';
import 'react-toastify/dist/ReactToastify.css';
import PresetCard from './components/PresetCard';

const PresetListPage: React.FC<{}> = () => {
  const { username, hasRole } = useAuthStore();

  const [presets, setPresets] = useState<Preset[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [filterStatus, setFilterStatus] = useState(PresetFilter.all);

  const [selectedUser, setSelectedUser] = useState(USER_FILTER_ALL);

  const [selectedSort, setSelectedSort] = useState(SortOption.UPDATED_NEW_TO_OLD);

  const [statusDropdownOpen, setStatusDropdownOpen] = useState<number | null>(null);

  useEffect(() => {
    if (username !== '') {
      fetchPresets();
    }
  }, [username]);

  useEffect(() => {
    const handleClickOutside = () => {
      setStatusDropdownOpen(null);
    };

    if (statusDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [statusDropdownOpen]);

  const canViewAll = hasRole([Role.ADMIN, Role.VIEWER, Role.CRO]);

  const fetchPresets = async () => {
    try {
      const url = canViewAll
        ? 'https://prod-64.eastus.logic.azure.com:443/workflows/0e58819ec2514385b6ca626efe8fda5b/triggers/manual/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=S6p5Z2ohBaaZPjs7BEeU9h3nXNTzAQbPKWH_IQPNmaI'
        : `https://prod-19.eastus.logic.azure.com/workflows/fef96acc2ba64f03afd3d26935bdb154/triggers/manual/paths/invoke/${username}?api-version=2016-10-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=qHlHVQhytfagqXEB8oUGfUisBYz48Oj18Ci17Md5Wps`;

      const response = await fetch(url);

      const data: Preset[] = await response.json();

      setPresets(data);
    } catch (error) {
      //TODO: handle 'SyntaxError: JSON.parse' when no presets for user without giving error
      console.error('Error fetching presets:', error);
    }

    setIsLoading(false);
  };

  // Extract unique users from presets for the user filter
  const uniqueUsers = Array.from(new Set(presets.map(preset => preset.user))).sort();

  // Sort presets based on selected sort option
  const sortPresets = (presets: Preset[], sortOption: SortOption): Preset[] => {
    return [...presets].sort((a, b) => {
      switch (sortOption) {
        case SortOption.CREATED_NEW_TO_OLD:
          return new Date(b.created).getTime() - new Date(a.created).getTime();
        case SortOption.CREATED_OLD_TO_NEW:
          return new Date(a.created).getTime() - new Date(b.created).getTime();
        case SortOption.UPDATED_NEW_TO_OLD:
          return new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime();
        case SortOption.UPDATED_OLD_TO_NEW:
          return new Date(a.lastUpdated).getTime() - new Date(b.lastUpdated).getTime();
        default:
          return new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime();
      }
    });
  };

  return (
    <div
      style={{
        textAlign: 'center',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Header />
      {username ? (
        presets.length == 0 ? (
          <div
            style={{
              ...styles.pageContainer,
              justifyContent: 'center',
              color: 'rgba(16, 29, 59, 0.6)',
              gap: '0.8vh',
              fontSize: '1.4vh',
              fontWeight: '400',
            }}
          >
            {isLoading && <img src={myGif} height={'30vh'} width={'30vh'} alt="My Animation" />}
            {isLoading ? 'Loading presets...' : 'No presets for this user yet.'}
          </div>
        ) : (
          <div style={styles.pageContainer}>
            <div
              style={{
                position: 'sticky',
                top: '1.4vh',
                background: '#f3f4f5',
                padding: '1vh',
                borderRadius: '1.6vh',
                marginTop: '1.4vh',
                zIndex: 100,
                display: 'flex',
                flexDirection: 'row',
                gap: '1vh',
              }}
            >
              <SortDropdown selectedSort={selectedSort} setSelectedSort={setSelectedSort} />
              <StatusFilter status={filterStatus} setStatus={setFilterStatus} canSeePrivat={true} />
              {canViewAll && (
                <UserFilter selectedUser={selectedUser} setSelectedUser={setSelectedUser} users={uniqueUsers} />
              )}
            </div>
            <div style={gridContainerStyle}>
              {sortPresets(presets, selectedSort)
                .filter(preset => {
                  // Status filter
                  let statusMatch = false;
                  if (filterStatus === PresetFilter.all) {
                    statusMatch = true;
                  } else if (filterStatus === PresetFilter.waitingExportApproval) {
                    statusMatch = preset.approvalStatus === ApprovalStatus.PENDING;
                  } else if (filterStatus === PresetFilter.waitingDiscountApproval) {
                    statusMatch = preset.approvalStatus === ApprovalStatus.APPROVED_CRO && preset.overallDiscount < 0;
                  } else if (filterStatus === PresetFilter.private) {
                    statusMatch = preset.approvalStatus === ApprovalStatus.PRIVATE;
                  } else if (filterStatus === PresetFilter.approvedApprover) {
                    statusMatch = preset.approvalStatus === ApprovalStatus.APPROVED_APPROVER;
                  } else if (filterStatus === PresetFilter.rejectedApprover) {
                    statusMatch = preset.approvalStatus === ApprovalStatus.REJECTED_APPROVER;
                  } else if (filterStatus === PresetFilter.approvedCRO) {
                    statusMatch = preset.approvalStatus === ApprovalStatus.APPROVED_CRO && preset.overallDiscount >= 0;
                  } else if (filterStatus === PresetFilter.rejectedCRO) {
                    statusMatch = preset.approvalStatus === ApprovalStatus.REJECTED_CRO;
                  }

                  // User filter
                  const userMatch = selectedUser === USER_FILTER_ALL || preset.user === selectedUser;

                  return statusMatch && userMatch;
                })
                .map(preset => {
                  return (
                    <PresetCard
                      preset={preset}
                      fetchPresets={fetchPresets}
                      statusDropdownOpen={statusDropdownOpen}
                      setStatusDropdownOpen={setStatusDropdownOpen}
                    />
                  );
                })}
            </div>
          </div>
        )
      ) : (
        <></>
      )}
    </div>
  );
};

const gridContainerStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, 45.9vh)',
  gap: '1.2vh',
  justifyContent: 'center',
  padding: '0.6vh 1.6vh',
  width: '100%',
};

const styles = {
  pageContainer: {
    width: '100%',
    margin: '0 auto',
    boxSizing: 'border-box' as 'border-box',
    backgroundColor: 'rgba(16, 29, 59, 0.05)',
    borderRadius: '0.8vh',
    overflowY: 'auto' as 'auto',
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as 'column',
    alignItems: 'center' as 'center',
  },
};

export default PresetListPage;
